import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Search, Users, MessageSquare, Sparkles, Send, DollarSign, Image, UserCog, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { money, fmtDateTime } from "@/lib/format";

const GROUPS = [
  ["fans", "Fan", Users, (x) => `@${x.username}`, (x) => money(x.total_spent), (x) => `/fans?q=${x.username}`],
  ["conversations", "Conversazioni", MessageSquare, (x) => `${(x.fan_name || "").trim() || `@${x.fan_username}`} ↔ @${x.account_username}`, (x) => x.creator_name, (x) => `/messages?c=${x.id}`],
  ["messages", "Messaggi", MessageSquare, (x) => x.text, (x) => fmtDateTime(x.created_at), (x) => `/messages?c=${x.conversation_id}`],
  ["creators", "Creator", Sparkles, (x) => x.name, () => "", (x) => `/creators/${x.id}`],
  ["chatters", "Utenti", UserCog, (x) => `${x.first_name} ${x.last_name}`, (x) => x.role, () => "/users"],
  ["accounts", "Account Telegram", Send, (x) => `@${x.username || x.label}`, (x) => x.status, () => "/telegram"],
  ["sales", "Vendite", DollarSign, (x) => `${x.type.toUpperCase()} ${money(x.amount)}`, (x) => `@${x.fan_username}`, (x) => `/fans?q=${x.fan_username}`],
  ["media", "Media", Image, (x) => x.title, (x) => x.kind, () => "/vault"],
];

export default function GlobalSearch({ open, setOpen }) {
  const [q, setQ] = useState("");
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  useEffect(() => {
    if (q.trim().length < 2) { setRes(null); return; }
    setLoading(true);
    const t = setTimeout(() => {
      api.get("/search", { params: { q } }).then((r) => setRes(r.data)).catch(() => setRes(null)).finally(() => setLoading(false));
    }, 220);
    return () => clearTimeout(t);
  }, [q]);
  const go = (to) => { setOpen(false); setQ(""); navigate(to); };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="p-0 max-w-xl bg-[#0D0E14] border-[#232738] overflow-hidden" data-testid="global-search-dialog">
        <div className="flex items-center gap-2 border-b border-[#232738] px-4">
          <Search className="h-4 w-4 text-slate-500" />
          <input autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca in tutta l'agenzia…" data-testid="global-search-input"
            className="flex-1 bg-transparent py-3.5 text-sm outline-none placeholder:text-slate-600" />
          {loading && <Loader2 className="h-4 w-4 animate-spin text-violet-400" />}
        </div>
        <div className="max-h-[60vh] overflow-y-auto thin-scroll p-2" data-testid="global-search-results">
          {!res && <p className="text-xs text-slate-500 p-4">Digita almeno 2 caratteri. Fan, creator, chatter, account, conversazioni, messaggi, vendite, media, script.</p>}
          {res && GROUPS.every(([k]) => !(res[k] || []).length) && <p className="text-xs text-slate-500 p-4">Nessun risultato</p>}
          {res && GROUPS.map(([k, label, Icon, title, sub, link]) => (res[k] || []).length > 0 && (
            <div key={k} className="mb-2">
              <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-500">{label}</div>
              {res[k].map((x) => (
                <button key={x.id} onClick={() => go(link(x))} data-testid={`search-result-${k}`}
                  className="w-full flex items-center gap-3 rounded-md px-2 py-2 text-left hover:bg-violet-600/15 hover:text-white">
                  <Icon className="h-4 w-4 text-violet-400 shrink-0" />
                  <span className="text-sm text-slate-200 truncate flex-1">{title(x)}</span>
                  <span className="text-xs text-slate-500 shrink-0">{sub(x)}</span>
                </button>
              ))}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
